import React, { memo } from 'react'
import { RelativeDateTime } from 'transfix-ui/components/RelativeDateTime'
import { IStop, IUser } from 'apps/fleet-planner/types'
import * as Types from 'apps/fleet-planner/types/activity'
import { Bullet } from 'components/atoms'
import { ActivitySubtext } from '../../components/load-details/activity/ActivitySubtext'

interface ILastActivityProps {
  activity?: Types.IActivity
  currentUser?: IUser
  loadStops?: IStop[]
}

export const lastActivityText = (
  activity?: Types.IActivity,
  isDriver?: boolean,
  actorName?: string
): string => {
  // actorName is required on every type of shipment except CREATE and IMPORT
  const actorNameRequired =
    !Types.isActivityShipmentImported(activity) && !Types.isActivityShipmentCreated(activity)
  if (!activity || (!actorName && actorNameRequired)) return ''
  const actorText = isDriver ? 'driver' : actorName

  let text = ''
  if (Types.isActivityStatusUpdated(activity)) {
    text = `Status updated by ${actorText}`
  } else if (Types.isActivityShared(activity)) {
    text = `Load shared with driver`
  } else if (Types.isActivityShipmentCreated(activity)) {
    if (!actorText) {
      text = 'Load created'
    } else text = `Load created by ${actorText}`
  } else if (Types.isActivityCheckInTimeUpdated(activity)) {
    const { metadata } = activity || {}
    const { stop } = metadata || {}
    const { checkedInAt } = stop || {}
    text = `Check-in time ${checkedInAt ? 'edited' : 'removed'} by ${actorText}`
  } else if (Types.isActivityCheckOutTimeUpdated(activity)) {
    const { metadata } = activity || {}
    const { stop } = metadata || {}
    const { checkedOutAt } = stop || {}
    text = `Check-out time ${checkedOutAt ? 'edited' : 'removed'} by ${actorText}`
  } else if (Types.isActivityShipmentImported(activity)) {
    const { metadata } = activity || {}
    const { source } = metadata || {}
    text = `Load imported from ${source}`
  }
  return text
}

export const LastActivity = memo<ILastActivityProps>(
  ({ activity, currentUser, loadStops = [] }) => {
    const { actorFullName, createdAt, normalizedActorId: actorId, normalizedActorType: actorType } =
      activity || {}
    const hasSubtext =
      Types.isActivityShared(activity) ||
      ((Types.isActivityCheckOutTimeUpdated(activity) ||
        Types.isActivityStatusUpdated(activity) ||
        Types.isActivityCheckInTimeUpdated(activity)) &&
        activity.metadata.stop)
    const isDriver = actorType === Types.ActorType.driver
    const actorText = actorId === currentUser?.id ? 'you' : actorFullName

    // this is to handle older loads which don't have an activities array
    if (!activity) {
      return <></>
    }

    return (
      <span>
        <RelativeDateTime datetime={createdAt || ''} isShorthand />
        <Bullet />
        {lastActivityText(activity, isDriver, actorText)}
        {hasSubtext && (
          <ActivitySubtext
            activity={activity}
            loadStops={loadStops}
            className='loads-list-activity-col'
            iconSize='small'
          />
        )}
      </span>
    )
  }
)
